import { useCallback, useEffect, useRef } from 'react';
import { reportTestRunDiagnostics, type TestRunDiagnostic } from '@api/appBuilder';
import type { HtmlSandboxError, SandboxRequestFailure } from '@components/HtmlSandboxViewer';

export const FLUSH_DELAY_MS = 2_000;
export const MAX_DIAGNOSTICS = 100;
const MAX_PER_POST = 20;
const MAX_OCCURRENCES = 1_000;

interface Options {
  readonly appKey: string;
  readonly runId: string | null;
  readonly enabled: boolean;
}

interface Pending {
  readonly entry: Omit<TestRunDiagnostic, 'occurrences'>;
  occurrences: number;
}

export const useTestRunDiagnostics = ({ appKey, runId, enabled }: Options) => {
  const target = useRef({ appKey, runId, enabled });
  const pending = useRef(new Map<string, Pending>());
  const seen = useRef(new Set<string>());
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const flush = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    const { appKey: key, runId: run } = target.current;
    const entries = [...pending.current.values()].map(({ entry, occurrences }) => ({ ...entry, occurrences }));
    pending.current.clear();
    if (!run) return;
    for (let at = 0; at < entries.length; at += MAX_PER_POST) {
      reportTestRunDiagnostics(key, run, entries.slice(at, at + MAX_PER_POST)).catch(() => undefined);
    }
  }, []);

  useEffect(() => {
    if (target.current.runId !== runId || target.current.appKey !== appKey) {
      flush();
      seen.current.clear();
    }
    target.current = { appKey, runId, enabled };
  }, [appKey, runId, enabled, flush]);

  useEffect(() => flush, [flush]);

  const add = useCallback(
    (entry: Omit<TestRunDiagnostic, 'occurrences'>) => {
      if (!target.current.enabled || !target.current.runId) return;
      const key = `${entry.kind}|${entry.code ?? ''}|${entry.message}`;
      const known = pending.current.get(key);
      if (known) {
        known.occurrences = Math.min(known.occurrences + 1, MAX_OCCURRENCES);
      } else {
        if (seen.current.size >= MAX_DIAGNOSTICS && !seen.current.has(key)) return;
        seen.current.add(key);
        pending.current.set(key, { entry, occurrences: 1 });
      }
      timer.current ??= setTimeout(flush, FLUSH_DELAY_MS);
    },
    [flush],
  );

  const onError = useCallback(
    (error: HtmlSandboxError) =>
      add({ kind: error.source === 'kit' ? 'KIT_ERROR' : 'PAGE_ERROR', message: error.message }),
    [add],
  );
  const onRequestFailed = useCallback(
    (failure: SandboxRequestFailure) =>
      add({ kind: 'REQUEST_FAILED', method: failure.method, code: failure.code, message: failure.message }),
    [add],
  );
  return { onError, onRequestFailed };
};
